import React from 'react';
import { buildStatusTimeline } from '../utils/orders';
import { formatOrderTitle } from '../utils/orderRef';

const fmt = (d) => (d ? d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

const statusColor = (s) => {
  if (s === 'delivered') return 'badge-green';
  if (s === 'pending') return 'badge-orange';
  if (s === 'cancelled' || s === 'failed') return 'badge-red';
  if (['confirmed', 'picked_up'].includes(s)) return 'badge-blue';
  return 'badge-gray';
};

export default function OrderDetailModal({ order, onClose }) {
  if (!order) return null;
  const timeline = buildStatusTimeline(order);

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={onClose}
    >
      <div
        className="table-card"
        style={{ maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 24 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ fontFamily: 'Clash Display, sans-serif', fontSize: 18, fontWeight: 700, marginBottom: 4 }}>
              {formatOrderTitle(order)}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text2)' }}>{order.shopName || 'Shop'}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className={`badge ${statusColor(order.status)}`}>{order.status}</span>
            <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text2)', cursor: 'pointer', fontSize: 20 }}>✕</button>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: 'var(--text2)', fontFamily: 'JetBrains Mono', marginBottom: 8, letterSpacing: 1 }}>ITEMS</div>
          {(order.items || []).map((item, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
              <span>{item.emoji || ''} {item.name} × {item.qty}</span>
              <span style={{ fontFamily: 'JetBrains Mono', color: 'var(--green)' }}>₹{(item.price || 0) * (item.qty || 1)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, fontSize: 13 }}>
            <span style={{ color: 'var(--text2)' }}>Subtotal</span>
            <span style={{ fontFamily: 'JetBrains Mono' }}>₹{order.subtotal ?? '—'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 4, fontSize: 13 }}>
            <span style={{ color: 'var(--text2)' }}>Delivery fee</span>
            <span style={{ fontFamily: 'JetBrains Mono' }}>₹{order.deliveryFee ?? '—'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, fontSize: 15, fontWeight: 700 }}>
            <span>Total</span>
            <span style={{ fontFamily: 'JetBrains Mono', color: 'var(--green)' }}>₹{order.total}</span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div style={{ padding: 12, background: 'var(--surface)', borderRadius: 8 }}>
            <div style={{ fontSize: 10, color: 'var(--text2)', fontFamily: 'JetBrains Mono', marginBottom: 4 }}>CUSTOMER</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{order.customerName || '—'}</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', fontFamily: 'JetBrains Mono' }}>{order.customerPhone || '—'}</div>
          </div>
          <div style={{ padding: 12, background: 'var(--surface)', borderRadius: 8 }}>
            <div style={{ fontSize: 10, color: 'var(--text2)', fontFamily: 'JetBrains Mono', marginBottom: 4 }}>PAYMENT</div>
            <span className={`badge ${order.paymentMode === 'cash' ? 'badge-gray' : 'badge-blue'}`}>{order.paymentMode || 'cash'}</span>
            {order.paymentStatus && (
              <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 6 }}>{order.paymentStatus}</div>
            )}
          </div>
        </div>

        <div style={{ padding: 12, background: 'var(--surface)', borderRadius: 8, marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: 'var(--text2)', fontFamily: 'JetBrains Mono', marginBottom: 4 }}>DELIVERY ADDRESS</div>
          <div style={{ fontSize: 13 }}>{order.deliveryAddress || '—'}</div>
          {order.deliveryPartnerId && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
              <div style={{ fontSize: 10, color: 'var(--text2)', fontFamily: 'JetBrains Mono', marginBottom: 4 }}>RIDER</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{order.deliveryPartnerName || 'Assigned'}</div>
              <div style={{ fontSize: 11, color: 'var(--text2)', fontFamily: 'JetBrains Mono' }}>{order.deliveryPartnerPhone || order.deliveryPartnerId.slice(0, 12)}</div>
            </div>
          )}
        </div>

        <div>
          <div style={{ fontSize: 10, color: 'var(--text2)', fontFamily: 'JetBrains Mono', marginBottom: 10, letterSpacing: 1 }}>STATUS TIMELINE</div>
          {timeline.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text2)' }}>No timeline data</div>
          ) : timeline.map((step, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--green)', marginTop: 5, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{step.label}</div>
                <div style={{ fontSize: 11, color: 'var(--text2)', fontFamily: 'JetBrains Mono' }}>{fmt(step.at)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
